import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import Input from '../Input';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import useModal from 'src/hooks/useModal';
import { bulkCutout } from 'src/api/functions/addonsApi';
import { useAiRatesQuery } from 'src/api/queries/subscriptionQueries';
import {
  CUTOUT_OUTCOMES,
  CutoutOutcome,
  MatteBackground,
  previewCreditsFor,
} from 'src/pages/Asset/components/cutoutOutcomes';
import { validateResultName } from 'src/pages/Asset/components/SaveResultForm';
import messages from 'src/pages/Asset/messages';
import styles from './BulkCutoutModal.module.scss';

type BulkCutoutModalProps = BaseModalProps & {
  assetIds: string[];
};

type Disposition = 'add_new_asset' | 'add_conversion';

const DEFAULT_CONVERSION_NAME = 'cutout';

/**
 * Run a cut-out over the current selection.
 *
 * Matte outcomes only — the same two the asset panel routes to the 2-credit
 * engine. The generative outcomes need a subject, a prop list and a refine
 * prompt per photo, so "apply to all of these" has no single meaning; they stay
 * a per-asset operation.
 *
 * The work runs server-side one asset at a time, so this dialog's job is to
 * state the cost honestly, take the two decisions, and hand the user a link to
 * the job.
 */
export const BulkCutoutModal: React.FC<BulkCutoutModalProps> = ({ assetIds, ...modalProps }) => {
  const { formatMessage } = useIntl();
  const accessToken = useAccessToken();
  const history = useHistory();
  const { closeModal } = useModal();
  const { showAlert, showSuccess } = useAlerts();

  const matteOutcomes = useMemo(
    () => CUTOUT_OUTCOMES.filter((o) => o.engine === 'matte'),
    [],
  );
  const [outcome, setOutcome] = useState<CutoutOutcome>(matteOutcomes[0]);
  const [disposition, setDisposition] = useState<Disposition>('add_new_asset');
  const [conversionName, setConversionName] = useState(DEFAULT_CONVERSION_NAME);
  // Public by default: a cut-out is normally produced in order to be published.
  const [isPublic, setIsPublic] = useState(true);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const count = assetIds.length;
  /*
   * Per-image price from the live rate card (`bgremove` in `ai_credit_rates`),
   * not a constant: it is sysadmin-editable, and this modal quotes a total the
   * user is about to commit to. `undefined` until the card resolves — the cost
   * row is then left out rather than quoting a guessed total.
   */
  const { data: aiRates } = useAiRatesQuery();
  const perImage = previewCreditsFor(outcome, aiRates?.flat);
  const totalCredits = perImage === undefined ? undefined : perImage * count;

  const handleSubmit = async () => {
    if (submitting) return;
    if (disposition === 'add_conversion') {
      const problem = validateResultName(conversionName);
      if (problem) {
        setNameError(formatMessage(messages.aiEditNameInvalid));
        return;
      }
    }
    setNameError(undefined);
    setSubmitting(true);
    try {
      const queued = await bulkCutout(
        {
          ids: assetIds,
          background: outcome.matteBackground as MatteBackground,
          disposition,
          // Conversions only: the server always creates a new asset's original
          // private, so sending an access preference there would be a lie.
          ...(disposition === 'add_conversion'
            ? { conversion_name: conversionName, public: isPublic }
            : {}),
        },
        accessToken,
      );
      showSuccess(
        formatMessage(messages.cutoutBatchQueued, {
          count: queued.count,
          credits: queued.credits_required,
        }),
      );
      closeModal();
      // The job page is where progress and the per-asset outcomes live, so send
      // the user there rather than leaving them guessing on the asset list.
      history.push(`/job/${queued.job_id}`);
    } catch (e: any) {
      // The three refusals worth naming: not enough credits for the whole
      // batch, a batch already running, or the addon switched off.
      if (e?.status === 402) {
        const data = e?.body?.data || {};
        showAlert(
          formatMessage(messages.aiEditInsufficientCredits, {
            required: data.required ?? totalCredits ?? '?',
            balance: data.balance ?? '?',
          }),
        );
      } else if (e?.status === 409) {
        showAlert(formatMessage(messages.cutoutBatchAlreadyRunning));
      } else {
        showAlert(e?.message || formatMessage(messages.aiEditFailed));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal {...modalProps}>
      <div className={styles.body}>
        <h3 className={styles.title}>{formatMessage(messages.cutoutBatchTitle)}</h3>
        <p className={styles.lede}>
          {formatMessage(messages.cutoutBatchLede, { count })}
        </p>

        <div className={styles.field}>
          <span className={styles.label}>{formatMessage(messages.cutoutPrompt)}</span>
          <div className={styles.options}>
            {matteOutcomes.map((o) => (
              <button
                key={o.id}
                type="button"
                className={o.id === outcome.id ? `${styles.option} ${styles.optionOn}` : styles.option}
                aria-pressed={o.id === outcome.id}
                onClick={() => setOutcome(o)}
                disabled={submitting}
                data-test={`bulk-cutout-${o.id}`}
              >
                <span className={styles.optionTitle}>{formatMessage(messages[o.labelMsg])}</span>
                <span className={styles.optionDesc}>{formatMessage(messages[o.descMsg])}</span>
              </button>
            ))}
          </div>
          <span className={styles.hint}>{formatMessage(messages.cutoutBatchMatteOnly)}</span>
        </div>

        <div className={styles.field}>
          <span className={styles.label}>{formatMessage(messages.cutoutBatchSaveAs)}</span>
          <div className={styles.options}>
            <button
              type="button"
              className={
                disposition === 'add_new_asset' ? `${styles.option} ${styles.optionOn}` : styles.option
              }
              aria-pressed={disposition === 'add_new_asset'}
              onClick={() => setDisposition('add_new_asset')}
              disabled={submitting}
              data-test="bulk-cutout-new-assets"
            >
              <span className={styles.optionTitle}>
                {formatMessage(messages.cutoutBatchNewAssets)}
              </span>
              <span className={styles.optionDesc}>
                {formatMessage(messages.cutoutBatchNewAssetsDesc)}
              </span>
            </button>
            <button
              type="button"
              className={
                disposition === 'add_conversion' ? `${styles.option} ${styles.optionOn}` : styles.option
              }
              aria-pressed={disposition === 'add_conversion'}
              onClick={() => setDisposition('add_conversion')}
              disabled={submitting}
              data-test="bulk-cutout-conversions"
            >
              <span className={styles.optionTitle}>
                {formatMessage(messages.cutoutBatchConversions)}
              </span>
              <span className={styles.optionDesc}>
                {formatMessage(messages.cutoutBatchConversionsDesc)}
              </span>
            </button>
          </div>
        </div>

        {disposition === 'add_conversion' && (
          <div className={styles.field}>
            <Input
              type="text"
              variant="secondary"
              label={formatMessage(messages.aiEditConversionName)}
              value={conversionName}
              onChange={setConversionName}
              error={nameError}
              disabled={submitting}
            />
          </div>
        )}

        {/* Conversions only: a format is what the dynamic image sizing API
            (/conversions) serves, so it is the thing worth publishing. A new
            asset's original is always created private (the server forces it),
            so an access choice there would do nothing. */}
        {disposition === 'add_conversion' && (
        <div className={styles.field}>
          <span className={styles.label}>{formatMessage(messages.cutoutAccessLabel)}</span>
          <div className={styles.options}>
            <button
              type="button"
              className={isPublic ? `${styles.option} ${styles.optionOn}` : styles.option}
              aria-pressed={isPublic}
              onClick={() => setIsPublic(true)}
              disabled={submitting}
              data-test="bulk-cutout-public"
            >
              <span className={styles.optionTitle}>
                {formatMessage(messages.cutoutAccessPublic)}
              </span>
              <span className={styles.optionDesc}>
                {formatMessage(messages.cutoutAccessPublicHint)}
              </span>
            </button>
            <button
              type="button"
              className={!isPublic ? `${styles.option} ${styles.optionOn}` : styles.option}
              aria-pressed={!isPublic}
              onClick={() => setIsPublic(false)}
              disabled={submitting}
              data-test="bulk-cutout-private"
            >
              <span className={styles.optionTitle}>
                {formatMessage(messages.cutoutAccessPrivate)}
              </span>
              <span className={styles.optionDesc}>
                {formatMessage(messages.cutoutAccessPrivateHint)}
              </span>
            </button>
          </div>
        </div>
        )}

        {perImage !== undefined && totalCredits !== undefined && (
          <div className={styles.cost} data-test="bulk-cutout-cost">
            {formatMessage(messages.cutoutBatchCost, {
              count,
              rate: perImage,
              credits: totalCredits,
            })}
          </div>
        )}
        {/* Said before they commit, because it is the one surprise in the flow:
            the batch is refused outright rather than part-run. */}
        <p className={styles.hint}>{formatMessage(messages.cutoutBatchAllOrNothing)}</p>

        <div className={styles.actions}>
          <Button mode="flat" onClick={closeModal} disabled={submitting}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button mode="primary" onClick={handleSubmit} loading={submitting} disabled={!count}>
            {formatMessage(messages.cutoutBatchStart, { count })}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default BulkCutoutModal;
